//! Backoffice: coda di review dei market draft generati dal feed ingestion.
//!
//! Il flusso è: il worker ingerisce candidati dalle sorgenti esterne → qui
//! l'operator rivede/corregge (soprattutto le *resolution rules*, che finiscono
//! nell'ancillary data dell'oracolo) → approva o rifiuta → un admin pubblica il
//! market vero e proprio.
//!
//! Nota ADR-007: `manual` non è un oracolo ammesso, quindi la select delle
//! resolution source non lo offre (il backend risponderebbe 400).

import { useCallback, useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { Pill } from "@/components/Pill";
import { useStore } from "@/state/store";

/* ──────────────────────────── tipi API ──────────────────────────── */

export interface MarketDraftView {
  id: string;
  external_id: string;
  source: string;
  title: string;
  description: string;
  category: string;
  kind: string;
  outcomes: string[];
  open_at: string;
  close_at: string;
  resolve_after: string;
  resolution_source: string;
  resolution_payload: unknown;
  reference_url: string | null;
  status: "pending" | "approved" | "rejected" | "published";
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  published_market_id: string | null;
  created_at: string;
}

interface MarketDraftListResponse {
  items?: MarketDraftView[];
  next_cursor?: string | null;
}

interface FeedSyncResultView {
  source: string;
  candidates_fetched: number;
  candidates_new: number;
  news_fetched: number;
  news_new: number;
  error: string | null;
}

interface FeedSyncResponse {
  results?: FeedSyncResultView[];
}

interface PublishResponse {
  market_id: string;
  slug: string;
}

/* ──────────────────────────── costanti ──────────────────────────── */

type Tone = "neutral" | "yes" | "no" | "accent" | "warn";
type StatusFilter = MarketDraftView["status"] | "all";

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "pending", label: "In review" },
  { value: "approved", label: "Approvati" },
  { value: "rejected", label: "Rifiutati" },
  { value: "published", label: "Pubblicati" },
  { value: "all", label: "Tutti" },
];

const STATUS_TONE: Record<string, Tone> = {
  pending: "warn",
  approved: "accent",
  rejected: "no",
  published: "yes",
};

const CATEGORIES = [
  "politics",
  "crypto",
  "sports",
  "tech",
  "science",
  "culture",
  "macro",
  "other",
];

/** Oracoli ammessi. `manual` è escluso di proposito (ADR-007). */
const RESOLUTION_SOURCES = ["uma", "chainlink", "sportradar"];

/** Marker inserito dall'ingestion quando le regole vanno scritte a mano. */
const RULES_PLACEHOLDER = "REGOLE DI RISOLUZIONE DA DEFINIRE";

/* ──────────────────────────── helper ──────────────────────────── */

/** Estrae un messaggio leggibile da un body di errore, con fallback sullo status. */
async function extractError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string; message?: string };
    return body.detail ?? body.message ?? `Errore (${res.status})`;
  } catch {
    return `Errore (${res.status})`;
  }
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** ISO 8601 → valore per `<input type="datetime-local">` (ora locale). */
function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Valore di un `datetime-local` → ISO 8601 UTC. `null` se non parsabile. */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function prettyJson(value: unknown): string {
  if (value === null || value === undefined) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/* ──────────────────────────── pagina ──────────────────────────── */

export function MarketDraftsPage() {
  const auth = useAuth();
  const showToast = useStore((s) => s.showToast);

  const [drafts, setDrafts] = useState<MarketDraftView[]>([]);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [actionRowId, setActionRowId] = useState<string | null>(null);

  const [status, setStatus] = useState<StatusFilter>("pending");
  const [source, setSource] = useState("");
  const [category, setCategory] = useState("");
  const [knownSources, setKnownSources] = useState<string[]>([]);

  const [reviewing, setReviewing] = useState<MarketDraftView | null>(null);
  const [rejecting, setRejecting] = useState<MarketDraftView | null>(null);
  const [publishing, setPublishing] = useState<MarketDraftView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (status !== "all") params.set("status", status);
      if (source) params.set("source", source);
      if (category) params.set("category", category);

      const res = await authFetch(`/v1/admin/market-drafts?${params}`);
      if (!res.ok) throw new Error(await extractError(res));
      const data = (await res.json()) as MarketDraftListResponse;
      const items = Array.isArray(data.items) ? data.items : [];
      setDrafts(items);

      // Le sorgenti note si accumulano: i filtri server-side restringono la
      // lista, ma la select deve continuare a offrire tutte le opzioni viste.
      setKnownSources((prev) => {
        const next = new Set(prev);
        for (const it of items) if (it.source) next.add(it.source);
        const arr = [...next].sort();
        const unchanged =
          arr.length === prev.length && arr.every((v, i) => v === prev[i]);
        return unchanged ? prev : arr;
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [status, source, category]);

  const loadPendingCount = useCallback(async () => {
    try {
      const res = await authFetch(
        "/v1/admin/market-drafts?status=pending&limit=200",
      );
      if (!res.ok) {
        setPendingCount(null);
        return;
      }
      const data = (await res.json()) as MarketDraftListResponse;
      setPendingCount(Array.isArray(data.items) ? data.items.length : 0);
    } catch {
      setPendingCount(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([load(), loadPendingCount()]);
  }, [load, loadPendingCount]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  async function handleSync() {
    setSyncing(true);
    try {
      const res = await authFetch("/v1/admin/feed/sync", { method: "POST" });
      if (!res.ok) throw new Error(await extractError(res));
      const data = (await res.json()) as FeedSyncResponse;
      const results = Array.isArray(data.results) ? data.results : [];

      const newDrafts = results.reduce((a, r) => a + (r.candidates_new ?? 0), 0);
      const newNews = results.reduce((a, r) => a + (r.news_new ?? 0), 0);
      const failed = results.filter((r) => r.error);
      const summary = `${results.length} ${
        results.length === 1 ? "sorgente" : "sorgenti"
      }: ${newDrafts} nuovi draft, ${newNews} notizie`;

      if (failed.length > 0) {
        showToast({
          kind: "error",
          message: `${summary} — errore su: ${failed
            .map((f) => f.source)
            .join(", ")}`,
        });
      } else {
        showToast({ kind: "success", message: summary });
      }
      await refresh();
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error
            ? `Sincronizzazione fallita: ${e.message}`
            : "Sincronizzazione fallita",
      });
    } finally {
      setSyncing(false);
    }
  }

  async function handleApprove(draft: MarketDraftView) {
    setActionRowId(draft.id);
    try {
      const res = await authFetch(
        `/v1/admin/market-drafts/${draft.id}/approve`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(await extractError(res));
      showToast({ kind: "success", message: "Draft approvato." });
      setReviewing(null);
      await refresh();
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error
            ? `Approvazione fallita: ${e.message}`
            : "Approvazione fallita",
      });
    } finally {
      setActionRowId(null);
    }
  }

  const columns: DataTableColumn<MarketDraftView>[] = [
    {
      key: "source",
      header: "Fonte",
      cell: (d) => <Pill tone="accent">{d.source}</Pill>,
      sortValue: (d) => d.source,
    },
    {
      key: "title",
      header: "Titolo",
      className: "max-w-[380px]",
      cell: (d) => (
        <span className="block truncate text-text" title={d.title}>
          {d.title}
        </span>
      ),
      sortValue: (d) => d.title.toLowerCase(),
    },
    {
      key: "category",
      header: "Categoria",
      cell: (d) => <span className="text-[12.5px] text-text-2">{d.category}</span>,
      sortValue: (d) => d.category,
    },
    {
      key: "close_at",
      header: "Chiusura",
      cell: (d) => (
        <span className="font-mono text-[11.5px] text-text-2">
          {formatDateTime(d.close_at)}
        </span>
      ),
      sortValue: (d) => new Date(d.close_at),
    },
    {
      key: "oracle",
      header: "Oracle",
      cell: (d) => (
        <span className="font-mono text-[11.5px] text-muted">
          {d.resolution_source}
        </span>
      ),
      sortValue: (d) => d.resolution_source,
    },
    {
      key: "status",
      header: "Stato",
      cell: (d) => (
        <Pill tone={STATUS_TONE[d.status] ?? "neutral"}>{d.status}</Pill>
      ),
      sortValue: (d) => d.status,
    },
    {
      key: "actions",
      header: "Azioni",
      align: "right",
      cell: (d) => (
        <div className="inline-flex gap-2 justify-end">
          <button
            type="button"
            className="btn-secondary py-1 px-3 text-[11.5px]"
            onClick={(e) => {
              e.stopPropagation();
              setReviewing(d);
            }}
          >
            Rivedi
          </button>
          {d.status === "pending" && (
            <>
              <button
                type="button"
                className="btn-secondary py-1 px-3 text-[11.5px] border-yes/40 text-yes hover:bg-yes/10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleApprove(d);
                }}
                disabled={actionRowId === d.id}
              >
                Approva
              </button>
              <button
                type="button"
                className="btn-secondary py-1 px-3 text-[11.5px] border-no/40 text-no hover:bg-no/10"
                onClick={(e) => {
                  e.stopPropagation();
                  setRejecting(d);
                }}
                disabled={actionRowId === d.id}
              >
                Rifiuta
              </button>
            </>
          )}
          {d.status === "approved" && auth.role === "admin" && (
            <button
              type="button"
              className="btn-primary py-1 px-3 text-[11.5px]"
              onClick={(e) => {
                e.stopPropagation();
                setPublishing(d);
              }}
              disabled={actionRowId === d.id}
            >
              Pubblica
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4 max-w-[1400px]">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-[22px] font-semibold">Market Drafts</h1>
            <Pill tone={pendingCount ? "warn" : "neutral"}>
              {pendingCount === null ? "— in review" : `${pendingCount} in review`}
            </Pill>
          </div>
          <p className="text-[13px] text-muted mt-1">
            Candidati generati dal feed. Rivedi le regole di risoluzione prima di
            approvare: regole ambigue si traducono in dispute UMA.
          </p>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing && <Spinner />}
          {syncing ? "Sincronizzazione…" : "Sincronizza feed"}
        </button>
      </header>

      {/* Filtri */}
      <section className="card flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className={
                status === f.value
                  ? "pill pill-accent cursor-pointer"
                  : "pill cursor-pointer hover:text-text"
              }
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5">
            <span className="text-[11.5px] text-muted">Fonte</span>
            <select
              className="input w-auto py-1 text-[12px]"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="">Tutte</option>
              {knownSources.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-1.5">
            <span className="text-[11.5px] text-muted">Categoria</span>
            <select
              className="input w-auto py-1 text-[12px]"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">Tutte</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento draft fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <DataTable
          rows={drafts}
          columns={columns}
          rowKey={(d) => d.id}
          pageSize={25}
          onRowClick={(d) => setReviewing(d)}
          empty={
            <div className="px-4 py-8 text-center text-muted text-[13px]">
              Nessun draft per i filtri selezionati. Lancia una sincronizzazione
              del feed per generarne di nuovi.
            </div>
          }
        />
      )}

      {reviewing && (
        <ReviewDrawer
          draft={reviewing}
          onClose={() => setReviewing(null)}
          onSaved={(updated) => {
            setReviewing(updated);
            refresh();
          }}
          onApprove={handleApprove}
          onReject={(d) => setRejecting(d)}
        />
      )}

      {rejecting && (
        <RejectDraftModal
          draft={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            setReviewing(null);
            showToast({ kind: "success", message: "Draft rifiutato." });
            refresh();
          }}
        />
      )}

      {publishing && (
        <PublishDraftModal
          draft={publishing}
          onClose={() => setPublishing(null)}
          onDone={(slug) => {
            setPublishing(null);
            setReviewing(null);
            showToast({
              kind: "success",
              message: `Market pubblicato: ${slug}`,
            });
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ──────────────────────── sub-components ───────────────────────── */

/** Spinner inline minimale (niente classi globali nuove). */
function Spinner() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 rounded-full border-2 border-white/30 border-t-white animate-spin"
    />
  );
}

/**
 * Drawer di review: è qui che l'operator corregge titolo, descrizione e
 * finestre temporali prima di approvare. La `description` finisce nell'ancillary
 * data dell'oracolo, quindi è il campo più delicato della pagina.
 */
function ReviewDrawer({
  draft,
  onClose,
  onSaved,
  onApprove,
  onReject,
}: {
  draft: MarketDraftView;
  onClose: () => void;
  onSaved: (updated: MarketDraftView) => void;
  onApprove: (draft: MarketDraftView) => void | Promise<void>;
  onReject: (draft: MarketDraftView) => void;
}) {
  const showToast = useStore((s) => s.showToast);

  const [title, setTitle] = useState(draft.title);
  const [description, setDescription] = useState(draft.description);
  const [category, setCategory] = useState(draft.category);
  const [closeAt, setCloseAt] = useState(toDatetimeLocal(draft.close_at));
  const [resolveAfter, setResolveAfter] = useState(
    toDatetimeLocal(draft.resolve_after),
  );
  const [resolutionSource, setResolutionSource] = useState(
    draft.resolution_source,
  );
  const [saving, setSaving] = useState(false);

  const closeMs = Date.parse(closeAt);
  const resolveMs = Date.parse(resolveAfter);
  let dateError: string | null = null;
  if (!closeAt || !resolveAfter) {
    dateError = "Chiusura e risoluzione sono obbligatorie.";
  } else if (
    Number.isFinite(closeMs) &&
    Number.isFinite(resolveMs) &&
    resolveMs < closeMs
  ) {
    dateError = "«Risolvi dopo» deve essere uguale o successivo alla chiusura.";
  }

  const needsRules = description.includes(RULES_PLACEHOLDER);
  const isPending = draft.status === "pending";

  // La select degli oracoli non offre `manual`; se un draft legacy lo porta
  // comunque, lo mostriamo come opzione extra per non cambiarlo in silenzio.
  const sourceOptions = RESOLUTION_SOURCES.includes(resolutionSource)
    ? RESOLUTION_SOURCES
    : [...RESOLUTION_SOURCES, resolutionSource];

  async function handleSave() {
    if (dateError) return;
    const patch: Record<string, unknown> = {};
    if (title !== draft.title) patch.title = title;
    if (description !== draft.description) patch.description = description;
    if (category !== draft.category) patch.category = category;
    if (closeAt !== toDatetimeLocal(draft.close_at)) {
      patch.close_at = localToIso(closeAt);
    }
    if (resolveAfter !== toDatetimeLocal(draft.resolve_after)) {
      patch.resolve_after = localToIso(resolveAfter);
    }
    if (resolutionSource !== draft.resolution_source) {
      patch.resolution_source = resolutionSource;
    }

    if (Object.keys(patch).length === 0) {
      showToast({ kind: "info", message: "Nessuna modifica da salvare." });
      return;
    }

    setSaving(true);
    try {
      const res = await authFetch(`/v1/admin/market-drafts/${draft.id}`, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(await extractError(res));

      // Ricarica il draft aggiornato per rinfrescare anche i campi read-only.
      let updated: MarketDraftView = {
        ...draft,
        title,
        description,
        category,
        close_at: localToIso(closeAt) ?? draft.close_at,
        resolve_after: localToIso(resolveAfter) ?? draft.resolve_after,
        resolution_source: resolutionSource,
      };
      try {
        const fresh = await authFetch(`/v1/admin/market-drafts/${draft.id}`);
        if (fresh.ok) updated = (await fresh.json()) as MarketDraftView;
      } catch {
        /* best-effort: teniamo la versione ottimistica */
      }

      showToast({ kind: "success", message: "Modifiche salvate." });
      onSaved(updated);
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error
            ? `Salvataggio fallito: ${e.message}`
            : "Salvataggio fallito",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-[720px] overflow-y-auto border-l border-line bg-bg-1 p-5 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Pill tone="accent">{draft.source}</Pill>
            <Pill tone={STATUS_TONE[draft.status] ?? "neutral"}>
              {draft.status}
            </Pill>
          </div>
          <button
            type="button"
            className="text-muted hover:text-text text-[20px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>

        {needsRules && (
          <div className="card border-no/50 bg-no/10 text-[12.5px] text-no">
            ⚠️ Questo draft richiede che tu definisca le regole di risoluzione
            prima di approvarlo
          </div>
        )}

        <label className="flex flex-col gap-1">
          <span className="text-[11.5px] text-muted">Titolo</span>
          <input
            className="input text-[15px]"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="flex items-center justify-between gap-2">
            <span className="text-[11.5px] text-muted">Descrizione</span>
            <span className="text-[11px] text-muted">
              Testo inviato nell&apos;ancillary data dell&apos;oracolo
            </span>
          </span>
          <textarea
            className="input min-h-[220px] resize-y leading-relaxed"
            rows={10}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Categoria</span>
            <select
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={saving}
            >
              {(CATEGORIES.includes(category)
                ? CATEGORIES
                : [...CATEGORIES, category]
              ).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Resolution source</span>
            <select
              className="input"
              value={resolutionSource}
              onChange={(e) => setResolutionSource(e.target.value)}
              disabled={saving}
            >
              {sourceOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Chiusura</span>
            <input
              className="input"
              type="datetime-local"
              value={closeAt}
              onChange={(e) => setCloseAt(e.target.value)}
              disabled={saving}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[11.5px] text-muted">Risolvi dopo</span>
            <input
              className="input"
              type="datetime-local"
              value={resolveAfter}
              onChange={(e) => setResolveAfter(e.target.value)}
              disabled={saving}
            />
          </label>
        </div>

        {dateError && (
          <div className="text-[12px] text-no">{dateError}</div>
        )}

        {/* Read-only */}
        <section className="card bg-bg-2 flex flex-col gap-3">
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[12px]">
            <dt className="text-muted">External ID</dt>
            <dd className="font-mono text-text-2 break-all">
              {draft.external_id}
            </dd>
            <dt className="text-muted">Fonte</dt>
            <dd className="font-mono text-text-2">{draft.source}</dd>
            <dt className="text-muted">Tipo</dt>
            <dd className="font-mono text-text-2">{draft.kind}</dd>
            <dt className="text-muted">Creato</dt>
            <dd className="font-mono text-text-2">
              {formatDateTime(draft.created_at)}
            </dd>
            {draft.reviewed_at && (
              <>
                <dt className="text-muted">Revisionato</dt>
                <dd className="font-mono text-text-2">
                  {formatDateTime(draft.reviewed_at)}
                  {draft.reviewed_by ? ` · ${draft.reviewed_by}` : ""}
                </dd>
              </>
            )}
            {draft.review_note && (
              <>
                <dt className="text-muted">Nota</dt>
                <dd className="text-text-2">{draft.review_note}</dd>
              </>
            )}
            {draft.published_market_id && (
              <>
                <dt className="text-muted">Market</dt>
                <dd className="font-mono text-text-2 break-all">
                  {draft.published_market_id}
                </dd>
              </>
            )}
          </dl>

          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11.5px] text-muted mr-1">Outcomes</span>
            {draft.outcomes.length === 0 ? (
              <span className="text-[12px] text-muted">—</span>
            ) : (
              draft.outcomes.map((o) => (
                <Pill key={o} tone="neutral">
                  {o}
                </Pill>
              ))
            )}
          </div>

          {draft.reference_url && (
            <a
              className="text-[12px] text-accent-2 hover:underline break-all"
              href={draft.reference_url}
              target="_blank"
              rel="noreferrer"
            >
              {draft.reference_url} ↗
            </a>
          )}

          <details className="text-[12px]">
            <summary className="cursor-pointer text-muted hover:text-text">
              resolution_payload
            </summary>
            <pre className="mt-2 max-h-[240px] overflow-auto rounded-md bg-bg-3 p-3 font-mono text-[11px] text-text-2 whitespace-pre-wrap break-all">
              {prettyJson(draft.resolution_payload)}
            </pre>
          </details>
        </section>

        {/* Azioni */}
        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Chiudi
          </button>
          {isPending && (
            <button
              type="button"
              className="btn-secondary border-no/40 text-no hover:bg-no/10"
              onClick={() => onReject(draft)}
              disabled={saving}
            >
              Rifiuta
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={handleSave}
            disabled={saving || Boolean(dateError)}
            title={dateError ?? undefined}
          >
            {saving ? "Salvataggio…" : "Salva modifiche"}
          </button>
          {isPending && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => onApprove(draft)}
              disabled={saving || needsRules}
              title={
                needsRules
                  ? "Definisci le regole di risoluzione prima di approvare"
                  : undefined
              }
            >
              Approva
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Rifiuto con motivazione obbligatoria (finisce nel `review_note`). */
function RejectDraftModal({
  draft,
  onClose,
  onDone,
}: {
  draft: MarketDraftView;
  onClose: () => void;
  onDone: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleReject() {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      const res = await authFetch(
        `/v1/admin/market-drafts/${draft.id}/reject`,
        { method: "POST", body: JSON.stringify({ reason: reason.trim() }) },
      );
      if (!res.ok) throw new Error(await extractError(res));
      onDone();
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error ? `Rifiuto fallito: ${e.message}` : "Rifiuto fallito",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[460px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Rifiuta draft</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <div className="text-[12px] mb-3">
          <div className="text-muted">Draft</div>
          <div className="text-text-2">{draft.title}</div>
        </div>
        <label className="flex flex-col gap-1 mb-4">
          <span className="text-[11.5px] text-muted">Motivazione</span>
          <textarea
            className="input min-h-[96px] resize-y"
            placeholder="es. domanda ambigua, evento già risolto, fonte non affidabile…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleReject}
            disabled={submitting || !reason.trim()}
          >
            {submitting ? "Invio…" : "Rifiuta"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Conferma di pubblicazione (admin-only): crea il market vero e proprio. */
function PublishDraftModal({
  draft,
  onClose,
  onDone,
}: {
  draft: MarketDraftView;
  onClose: () => void;
  onDone: (slug: string) => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [submitting, setSubmitting] = useState(false);

  async function handlePublish() {
    setSubmitting(true);
    try {
      const res = await authFetch(
        `/v1/admin/market-drafts/${draft.id}/publish`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(await extractError(res));
      const data = (await res.json()) as PublishResponse;
      onDone(data.slug ?? data.market_id ?? draft.id);
    } catch (e) {
      showToast({
        kind: "error",
        message:
          e instanceof Error
            ? `Pubblicazione fallita: ${e.message}`
            : "Pubblicazione fallita",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[460px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Pubblica market</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <p className="text-[13px] text-text-2 mb-3">
          Il draft diventerà un market visibile agli utenti. L&apos;operazione
          non è reversibile.
        </p>
        <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[12px] mb-4">
          <dt className="text-muted">Titolo</dt>
          <dd className="text-text-2">{draft.title}</dd>
          <dt className="text-muted">Chiusura</dt>
          <dd className="font-mono text-text-2">
            {formatDateTime(draft.close_at)}
          </dd>
          <dt className="text-muted">Oracle</dt>
          <dd className="font-mono text-text-2">{draft.resolution_source}</dd>
        </dl>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handlePublish}
            disabled={submitting}
          >
            {submitting ? "Pubblicazione…" : "Pubblica"}
          </button>
        </div>
      </div>
    </div>
  );
}
